import { useCallback, useEffect, useState } from 'react';
import { CalendarClock, CalendarDays, Inbox, Info, Loader2, LogOut, PlayCircle } from 'lucide-react';
import { sessionHubService, HubMe, HubWelcome } from '../../services/sessionHub.service';
import { WelcomePlayer } from '../../components/session-hub/WelcomePlayer';
import { SharedItems } from '../../components/session-hub/SharedItems';
import { HubSessions } from '../../components/session-hub/HubSessions';

type Section = 'welcome' | 'sessions' | 'shared' | 'about';

const SECTIONS: Array<{ key: Section; label: string; icon: React.ElementType }> = [
  { key: 'welcome', label: 'Welcome', icon: PlayCircle },
  { key: 'sessions', label: 'Sessions', icon: CalendarDays },
  { key: 'shared', label: 'Shared with you', icon: Inbox },
  { key: 'about', label: 'About this hub', icon: Info },
];

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

const initials = (me: HubMe) => {
  const source = me.visitor.displayName || me.visitor.email.split('@')[0];
  return source.split(/[\s._-]+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join('') || '?';
};

function EmptyState({ icon: Icon, title, detail }: { icon: React.ElementType; title: string; detail: string }) {
  return (
    <div className="rounded-xl bg-white p-12 shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-200 text-center">
      <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
        <Icon size={28} className="text-slate-400" />
      </div>
      <p className="text-slate-500 text-sm">{title}</p>
      <p className="text-slate-400 text-xs mt-2">{detail}</p>
    </div>
  );
}

function HubSummary({ me, onOpen, sharedCount }: { me: HubMe; onOpen: (s: Section) => void; sharedCount: number | null }) {
  return (
    <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
      <div className="px-5 pt-4 pb-2">
        <h2 className="text-[11px] font-bold text-primary tracking-widest uppercase">Your hub</h2>
      </div>
      <dl className="divide-y divide-[#e2e8f0] text-sm">
        <div className="px-5 py-3 flex items-center justify-between gap-3">
          <dt className="text-slate-500 flex items-center gap-2"><CalendarClock size={16} className="text-slate-400" /> Open until</dt>
          <dd className="font-semibold text-navy">{fmtDate(me.hub.expiresAt)}</dd>
        </div>
        <div className="px-5 py-3 flex items-center justify-between gap-3">
          <dt className="text-slate-500 flex items-center gap-2"><Inbox size={16} className="text-slate-400" /> Shared with you</dt>
          <dd>
            <button type="button" onClick={() => onOpen('shared')} className="font-semibold text-primary hover:underline">
              {sharedCount === null ? 'View' : sharedCount === 0 ? 'Nothing yet' : `${sharedCount} item${sharedCount === 1 ? '' : 's'}`}
            </button>
          </dd>
        </div>
        <div className="px-5 py-3 flex items-center justify-between gap-3">
          <dt className="text-slate-500 flex items-center gap-2"><Info size={16} className="text-slate-400" /> Signed in as</dt>
          <dd className="font-semibold text-navy truncate max-w-[60%]" title={me.visitor.email}>{me.visitor.displayName || me.visitor.email}</dd>
        </div>
      </dl>
      <div className="px-5 py-3 border-t border-[#e2e8f0] bg-slate-50/50">
        <button type="button" onClick={() => onOpen('about')} className="text-sm font-semibold text-primary hover:underline">About this hub</button>
      </div>
    </div>
  );
}

function WelcomeSection({ link, me, onOpen, sharedCount }: { link: string; me: HubMe; onOpen: (s: Section) => void; sharedCount: number | null }) {
  const [welcome, setWelcome] = useState<HubWelcome | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    sessionHubService.welcome(link).then(setWelcome).catch((e) => setError(e.message));
  }, [link]);

  useEffect(load, [load]);

  if (!welcome && !error) {
    return <div className="flex justify-center items-center py-20 text-[#64748b]"><Loader2 size={22} className="animate-spin" /></div>;
  }

  const main = error
    ? <EmptyState icon={PlayCircle} title="We could not load the welcome." detail={error} />
    : !welcome!.video && !welcome!.message
      ? <EmptyState icon={PlayCircle} title="No welcome here yet." detail="Your Version 1 team will add one before your session." />
      : welcome!.video
        ? (
          <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden p-3 sm:p-4">
            <WelcomePlayer url={welcome!.video.url} captions={welcome!.captions} onExpired={load} />
          </div>
        )
        : <MessageCard text={welcome!.message!} />;

  // Wide screens: the video (or the message on its own) takes two thirds, the
  // message and a summary of the hub sit alongside, so the page is balanced.
  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
      <div className="xl:col-span-2">{main}</div>
      <div className="flex flex-col gap-6">
        {welcome?.video && welcome.message && <MessageCard text={welcome.message} />}
        <HubSummary me={me} onOpen={onOpen} sharedCount={sharedCount} />
      </div>
    </div>
  );
}

function MessageCard({ text }: { text: string }) {
  return (
    <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
      <div className="px-5 pt-4 pb-1">
        <h2 className="text-[11px] font-bold text-primary tracking-widest uppercase">A note from your Version 1 team</h2>
      </div>
      <p className="px-5 pb-5 pt-2 text-sm text-slate-600 whitespace-pre-line leading-relaxed">{text}</p>
    </div>
  );
}

export default function HubShell({ link, me, onSignOut }: { link: string; me: HubMe; onSignOut: () => void }) {
  const [section, setSection] = useState<Section>('welcome');
  const [sharedCount, setSharedCount] = useState<number | null>(null);

  useEffect(() => {
    sessionHubService.items(link).then((r) => setSharedCount(r.items.length)).catch(() => setSharedCount(null));
  }, [link]);
  const current = SECTIONS.find((s) => s.key === section)!;

  return (
    <div className="h-screen bg-surface-light text-slate-900 flex flex-col font-display overflow-hidden">
      <meta name="robots" content="noindex" />
      <meta name="referrer" content="no-referrer" />

      <header className="flex items-center justify-between bg-navy px-4 sm:px-6 py-3 shrink-0 h-16 shadow-md z-50">
        <div className="flex items-center gap-3 text-white min-w-0">
          <span className="text-white text-lg font-extrabold tracking-tight whitespace-nowrap">Version 1 Co-Creation</span>
          <span className="hidden md:inline text-white/40">/</span>
          <span className="hidden md:inline text-white/80 text-sm font-medium truncate">{me.hub.title}</span>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button" onClick={onSignOut} title="Sign out"
            className="flex items-center justify-center rounded-lg h-8 w-8 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white border border-white/10 transition-colors"
          >
            <LogOut size={16} />
          </button>
          <div
            className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-[#052831] font-bold text-xs border-2 border-primary/20"
            title={me.visitor.displayName || me.visitor.email}
          >
            {initials(me)}
          </div>
        </div>
      </header>

      {/* Phone: the sections as tabs under the top bar */}
      <nav className="md:hidden flex gap-1 overflow-x-auto bg-white border-b border-slate-200 px-2 py-2 shrink-0">
        {SECTIONS.map(({ key, label, icon: Icon }) => (
          <button
            key={key} type="button" onClick={() => setSection(key)}
            className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm whitespace-nowrap ${section === key ? 'bg-primary/10 text-primary font-semibold' : 'text-slate-600'}`}
          >
            <Icon size={16} /> {label}
          </button>
        ))}
      </nav>

      <div className="flex flex-1 overflow-hidden">
        <aside className="hidden md:flex w-64 bg-sidebar-bg border-r border-slate-200 flex-col shrink-0">
          <div className="p-3 flex flex-col gap-1 flex-1">
            <h2 className="text-[11px] font-bold text-primary tracking-widest uppercase px-3 mb-1 mt-2">Session Hub</h2>
            <nav className="flex flex-col gap-1">
              {SECTIONS.map(({ key, label, icon: Icon }) => {
                const active = section === key;
                return (
                  <button
                    key={key} type="button" onClick={() => setSection(key)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all w-full ${
                      active ? 'bg-white border border-primary/20 shadow-sm text-primary' : 'text-slate-600 hover:bg-white hover:text-slate-900 hover:shadow-sm'
                    }`}
                  >
                    <Icon size={20} className={active ? 'text-primary' : 'text-slate-400'} />
                    <span className={`text-sm ${active ? 'font-semibold' : 'font-medium'}`}>{label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
          <div className="p-4 border-t border-slate-200 text-xs text-slate-400 flex items-center gap-2">
            <CalendarClock size={14} /> Open until {fmtDate(me.hub.expiresAt)}
          </div>
        </aside>

        <main className="flex-1 overflow-y-auto">
          <div className="p-4 sm:p-8">
            <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">
              <header className="flex items-center justify-between border-b border-slate-200 pb-6">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary flex-shrink-0">
                    <current.icon size={22} />
                  </div>
                  <div className="min-w-0">
                    <h1 className="text-2xl font-bold tracking-tight text-navy break-words">{section === 'welcome' ? me.hub.title : current.label}</h1>
                    <p className="text-slate-500 text-sm font-medium">
                      {section === 'welcome' ? `Welcome, ${me.visitor.displayName || me.visitor.email}` : me.hub.title}
                    </p>
                  </div>
                </div>
              </header>

              <div className="w-full">
                {section === 'welcome' && <WelcomeSection link={link} me={me} onOpen={setSection} sharedCount={sharedCount} />}
                {section === 'sessions' && <HubSessions link={link} />}
                {section === 'shared' && <SharedItems link={link} onCount={setSharedCount} />}
                {section === 'about' && (
                  <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
                    <dl className="divide-y divide-[#e2e8f0] text-sm">
                      <div className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-1">
                        <dt className="font-semibold text-navy">Signed in as</dt>
                        <dd className="sm:col-span-2 text-slate-600">{me.visitor.email}</dd>
                      </div>
                      <div className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-1">
                        <dt className="font-semibold text-navy">Open until</dt>
                        <dd className="sm:col-span-2 text-slate-600">{fmtDate(me.hub.expiresAt)}. After that the page closes and its content is no longer available here.</dd>
                      </div>
                      <div className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-1">
                        <dt className="font-semibold text-navy">Your privacy</dt>
                        <dd className="sm:col-span-2 text-slate-600">Only people your Version 1 team has named can sign in, each with a code sent to their own email. Sign-ins are recorded.</dd>
                      </div>
                      <div className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-1">
                        <dt className="font-semibold text-navy">Questions</dt>
                        <dd className="sm:col-span-2 text-slate-600">Contact your Version 1 team directly.</dd>
                      </div>
                    </dl>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
